import React, { useState, useRef, useEffect } from 'react';
import dropdownArrow from '../../assets/images/dropdown_down_grey.png';
import searchIcon from '../../assets/images/search.png';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  icon?: React.ReactNode;
  metadata?: Record<string, any>;
}

export interface SelectGroup {
  label: string;
  options: SelectOption[];
}

type SelectOptions = SelectOption[] | SelectGroup[];

interface SelectProps {
  options: SelectOptions;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  className?: string;
  attributes?: Record<string, any>;
  leftIcon?: string;
  rightIcon?: string;
  error?: boolean;
  showCustomOptions?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  isLoading?: boolean;
  onSearchChange?: (query: string) => void;
}

export const Select: React.FC<SelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select an option',
  disabled = false,
  className = '',
  attributes = {},
  leftIcon,
  rightIcon,
  error = false,
  showCustomOptions = false,
  searchable = false,
  searchPlaceholder = 'Search...',
  isLoading = false,
  onSearchChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const selectRef = useRef<HTMLDivElement>(null);
  
  const baseClasses = 'px-4 py-2 rounded-lg text-dark bg-light transition-colors duration-300 font-sora w-full appearance-none';
  
  const borderClass = error ? 'border-danger' : 'border-grey_light';
  const focusClass = error ? '' : '';

  // Flatten options for easier processing
  const flattenOptions = (opts: SelectOptions): SelectOption[] => {
    if (opts.length === 0) return [];
    
    // Check if first item is a group
    if ('label' in opts[0] && 'options' in opts[0]) {
      return (opts as SelectGroup[]).flatMap(group => group.options);
    }
    
    return opts as SelectOption[];
  };

  // Filter options based on search query
  const filterOptions = (opts: SelectOptions): SelectOptions => {
    if (!searchQuery.trim()) return opts;
    
    const query = searchQuery.toLowerCase();
    const flatOptions = flattenOptions(opts);
    
    const filtered = flatOptions.filter(option => 
      option.label.toLowerCase().includes(query) ||
      option.value.toLowerCase().includes(query) ||
      (option.metadata && 
        Object.values(option.metadata).some(val => 
          typeof val === 'string' && val.toLowerCase().includes(query)
        )
      )
    );
    
    return filtered;
  };

  const filteredOptions = searchable ? filterOptions(options) : options;
  const allOptions = flattenOptions(filteredOptions);
  
  // Find the selected option for custom display
  const selectedOption = allOptions.find(opt => opt.value === value);

  const handleChange = (newValue: string) => {
    onChange(newValue);
    setIsOpen(false);
    setSearchQuery('');
    if (onSearchChange) {
      onSearchChange('');
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
        if (onSearchChange) {
          onSearchChange('');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [onSearchChange]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const query = e.target.value;
    setSearchQuery(query);
    if (onSearchChange) {
      onSearchChange(query);
    }
  };

  const renderOptions = (opts: SelectOptions) => {
    if (opts.length === 0) {
      return (
        <div className="px-4 py-2 text-grey_dark text-sm">
          No options found
        </div>
      );
    }

    // Check if options are grouped
    if ('label' in opts[0] && 'options' in opts[0]) {
      return (opts as SelectGroup[]).map((group, groupIndex) => (
        <div key={groupIndex}>
          {group.label && (
            <div className="px-3 py-1 text-xs font-medium text-grey_dark bg-grey_light">
              {group.label}
            </div>
          )}
          {group.options.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => handleChange(option.value)}
              disabled={option.disabled}
              className={`w-full text-left px-4 py-2 hover:bg-grey_light transition-colors flex items-center ${option.value === value ? ' text-medium' : ''} ${option.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              {option.icon && (
                <span className="mr-2">{option.icon}</span>
              )}
              <span>{option.label}</span>
            </button>
          ))}
        </div>
      ));
    }

    // Render flat options
    return (opts as SelectOption[]).map((option) => (
      <button
        key={option.value}
        type="button"
        onClick={() => handleChange(option.value)}
        disabled={option.disabled}
        className={`w-full text-left px-4 py-2 hover:bg-grey_light transition-colors flex items-center ${option.value === value ? ' text-dark' : ''} ${option.disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
      >
        {option.icon && (
          <span className="mr-2">{option.icon}</span>
        )}
        <span>{option.label}</span>
      </button>
    ));
  };

  return (
    <div className="relative" ref={selectRef}>
      {/* Custom dropdown trigger */}
      <button
        type="button"
        onClick={() => !disabled && !isLoading && setIsOpen(!isOpen)}
        disabled={disabled || isLoading}
        className={`${baseClasses} border ${borderClass} ${focusClass} ${className} ${leftIcon ? 'pl-10' : ''} ${rightIcon ? 'pr-10' : ''} ${disabled || isLoading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'} flex items-center justify-between`}
        style={{
          backgroundImage: `url(${dropdownArrow})`,
          backgroundRepeat: 'no-repeat',
          backgroundPosition: 'right 0.75rem center',
          backgroundSize: '1rem',
          paddingRight: '2.5rem',
        }}
        {...attributes}
      >
        {/* Left Icon */}
        {leftIcon && (
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 z-10">
            <img
              src={leftIcon}
              alt=""
              className="w-5 h-5"
              onError={(e) => {
                console.error('Left icon failed to load:', leftIcon);
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Selected value display */}
        <div className="flex items-center truncate">
          {showCustomOptions && selectedOption ? (
            <>
              {selectedOption.icon && (
                <span className="mr-2">{selectedOption.icon}</span>
              )}
              <span className="truncate">{selectedOption.label}</span>
            </>
          ) : (
            <span className="truncate">
              {isLoading ? 'Loading...' : (selectedOption ? selectedOption.label : placeholder)}
            </span>
          )}
        </div>

        {/* Right Icon */}
        {rightIcon && (
          <div className="absolute right-10 top-1/2 transform -translate-y-1/2 z-10">
            <img
              src={rightIcon}
              alt=""
              className="w-5 h-5"
              onError={(e) => {
                console.error('Right icon failed to load:', rightIcon);
                e.currentTarget.style.display = 'none';
              }}
            />
          </div>
        )}

        {/* Loading spinner */}
        {isLoading && (
          <div className="absolute right-10 top-1/2 transform -translate-y-1/2">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary"></div>
          </div>
        )}
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-light border border-grey_light rounded shadow-lg max-h-60 overflow-y-auto">
          {/* Search input */}
          {searchable && (
            <div className=" border-b border-grey_light">
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  placeholder={searchPlaceholder}
                  className="w-full px-3 py-1 pr-8 text-sm border border-grey_light bg-light rounded"
                  autoFocus
                />
                <img
                  src={searchIcon}
                  alt="Search"
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 w-4 h-4 opacity-50"
                />
              </div>
            </div>
          )}

          {/* Options list */}
          <div className="py-1">
            {renderOptions(filteredOptions)}
          </div>
        </div>
      )}
    </div>
  );
};

export default Select;